import { RenewingType } from "@/api/enums/RenewingType";
import { GreenSwitchArrayValue } from "@/components/atoms/GreenSwitch";
import useChildForm from "@/hooks/ChildForm";
import { useCandidateCertificates } from "@/hooks/useCandidateCertificates";
import { Perimeter } from "@/lib/perimeter";
import { useEffect, useMemo, useState } from "react";
import { PerimeterForm } from "../../PerimeterForm/PerimeterForm";
import CertificateDisplay from "../CertificateDisplay";
import CertificatesSwitches from "../CertificatesSwitches";
import RenewingTypeRadio from "./RenewingTypeRadio";

interface RenewingPropertiesFormProps {
  candidateId?: number;
  fieldName?: string;
}

const RenewingPropertiesForm = ({
  fieldName,
  candidateId,
}: RenewingPropertiesFormProps) => {
  const [renewalAllowedPerimeter, setRenewalAllowedPerimeter] =
    useState<Perimeter>(Perimeter.empty());
  const { transformPath, watch, setValue } = useChildForm(fieldName);

  const { certificates, selectableCertificates, isPending, isError } =
    useCandidateCertificates(candidateId);

  const renewingType = watch(transformPath("renewingType"));
  const renewingCertificatesIDs = watch(
    transformPath("renewingCertificatesIDs"),
  );

  const validCertificates = useMemo(
    () => selectableCertificates ?? [],
    [selectableCertificates],
  );

  const withdrawnCertificates = useMemo(
    () =>
      (certificates ?? []).filter(
        ({ id }) =>
          renewingCertificatesIDs.includes(id) &&
          !validCertificates.some((certificate) => certificate.id === id),
      ),
    [certificates, renewingCertificatesIDs, validCertificates],
  );

  // merging perimeters is allowed only if the difference is
  // in merchandises or transport modes, not both
  const renewalAllowedCertificates = useMemo(() => {
    if (
      renewingCertificatesIDs.length === 0 ||
      renewingType !== RenewingType.FUSION
    )
      return validCertificates;

    const selectedCertificates = validCertificates.filter(({ id }) =>
      renewingCertificatesIDs.includes(id),
    );

    // FUSION needs validated certs that match the selected IDs; if none yet (loading or IDs not in that list), return all validated certs until they align.
    if (selectedCertificates.length === 0) return validCertificates;

    // Filter certificates to only include those that have a non-empty intersection
    // with ALL selected certificates
    return validCertificates.filter(({ id, perimeter }) => {
      // Always include already selected certificates
      if (renewingCertificatesIDs.includes(id)) return true;

      // Check if this certificate has a non-empty intersection with all selected certificates
      return selectedCertificates.every((selectedCert) => {
        const union = Perimeter.union(
          Perimeter.from(perimeter),
          Perimeter.from(selectedCert.perimeter),
        );
        return (
          (union.transportModes.length === perimeter.transportModes.length &&
            union.transportModes.length ===
              selectedCert.perimeter.transportModes.length) ||
          (union.merchandises.length === perimeter.merchandises.length &&
            union.merchandises.length ===
              selectedCert.perimeter.merchandises.length)
        );
      });
    });
  }, [validCertificates, renewingType, renewingCertificatesIDs]);

  // auto-select allowed fields
  useEffect(() => {
    const newAllowedPerimeter = Perimeter.union(
      ...(certificates
        ?.filter(({ id }) => renewingCertificatesIDs.includes(id as number))
        .map(({ perimeter }) => Perimeter.from(perimeter)) || []),
    );

    if ([RenewingType.SAME, RenewingType.FUSION].includes(renewingType)) {
      setValue(transformPath("perimeter"), newAllowedPerimeter);
    } else {
      const restrictedPerimeter = Perimeter.from(
        watch(transformPath("perimeter")),
      ).restrictByOther(newAllowedPerimeter);
      setValue(transformPath("perimeter"), restrictedPerimeter);
    }

    setRenewalAllowedPerimeter(newAllowedPerimeter);
  }, [
    renewingType,
    renewingCertificatesIDs,
    setValue,
    watch,
    certificates,
    transformPath,
  ]);

  // avoid multiple selections if forbidden
  useEffect(() => {
    if ([RenewingType.SAME, RenewingType.REDUCED].includes(renewingType)) {
      setValue(
        transformPath("renewingCertificatesIDs"),
        watch(transformPath("renewingCertificatesIDs")).slice(0, 1),
      );
    }
  }, [renewingType, setValue, watch, transformPath]);

  return (
    <>
      <RenewingTypeRadio fieldName={transformPath("renewingType")} />
      {isPending ? (
        <div>Chargement des certificats...</div>
      ) : isError ? (
        <div>Erreur lors du chargement des certificats.</div>
      ) : (
        <>
          <CertificatesSwitches
            certificates={renewalAllowedCertificates}
            unique={[RenewingType.SAME, RenewingType.REDUCED].includes(
              renewingType,
            )}
            fieldName={transformPath("renewingCertificatesIDs")}
            titleElement={<h2>Vos certificat(s) à renouveler</h2>}
          />
          {withdrawnCertificates.length > 0 && (
            <div className="flex flex-col w-full gap-2 flex-start self-stretch">
              <h2>Certificat(s) qui ne peuvent plus être renouvelés</h2>
              <p className="text-red-600 text-sm">
                Retirez-les de votre sélection pour poursuivre votre
                inscription.
              </p>
              {withdrawnCertificates.map((certificate) => (
                <GreenSwitchArrayValue
                  key={certificate.id}
                  name={transformPath("renewingCertificatesIDs")}
                  value={(certificate.id as number).toString()}
                  transform={(val) => Number(val)}
                  unique={[RenewingType.SAME, RenewingType.REDUCED].includes(
                    renewingType,
                  )}
                >
                  <CertificateDisplay certificate={certificate} />
                </GreenSwitchArrayValue>
              ))}
            </div>
          )}
          <PerimeterForm
            fieldName={transformPath("perimeter")}
            disabledPerimeter={
              [RenewingType.SAME, RenewingType.FUSION].includes(renewingType)
                ? Perimeter.full()
                : Perimeter.empty()
            }
            displayedPerimeter={renewalAllowedPerimeter}
            uncheckedAreRed={renewingType === RenewingType.REDUCED}
          />
        </>
      )}
    </>
  );
};

export default RenewingPropertiesForm;
